import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { RefreshCcw, Plus, Users, Clock, Swords } from 'lucide-react';
import { listRpsRooms, createRpsRoom, type RPSRoomSummary } from '@/lib/api/endpoints/rpsRooms';
import { queryKeys } from '@/lib/api/queryKeys';
import { Button } from '@/design-system/components/Button';
import { Modal } from '@/design-system/components/Modal';
import { Input } from '@/design-system/components/Input';
import { EmptyState } from '@/design-system/components/EmptyState';
import { Badge } from '@/design-system/components/Badge';
import { toast } from '@/store/uiStore';
import { extractApiError } from '@/lib/api/http';
import { t } from '@/lib/i18n';
import styles from './RpsRoomsPage.module.css';

const createRoomSchema = z.object({
  name: z.string().min(1, t('rpsRoom.nameRequired')).max(100),
  password: z.string().min(1, t('rpsRoom.passwordRequired')).max(20),
  max_games: z.preprocess((v) => (v === '' || v === null || v === undefined ? 3 : Number(v)), z.number().min(1).max(9)).default(3),
  time_limit_sec: z.preprocess((v) => (v === '' || v === null || v === undefined ? 10 : Number(v)), z.number().min(3).max(60)).default(10),
});

type CreateRoomForm = z.infer<typeof createRoomSchema>;

export default function RpsRoomsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data: rooms = [], isLoading, refetch } = useQuery({
    queryKey: queryKeys.rpsRooms.list,
    queryFn: listRpsRooms,
    refetchInterval: 10_000,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateRoomForm>({
    resolver: zodResolver(createRoomSchema),
    defaultValues: { name: '', password: '', max_games: 3, time_limit_sec: 10 },
  });

  const createMutation = useMutation({
    mutationFn: createRpsRoom,
    onSuccess: (room) => {
      qc.invalidateQueries({ queryKey: queryKeys.rpsRooms.list });
      toast.success(t('rpsRoom.createRoom'));
      setShowCreate(false);
      reset();
      navigate(`/rps-rooms/${room.room_id}`);
    },
    onError: (err) => toast.error(t('common.error'), extractApiError(err).message),
  });

  const onSubmit = (values: CreateRoomForm) => {
    createMutation.mutate(values);
  };

  const handleOpenRoom = (room: RPSRoomSummary) => {
    navigate(`/rps-rooms/${room.room_id}`);
  };

  return (
    <div className={styles.root}>
      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>{t('rpsRoom.pageTitle')}</h1>
        <p className={styles.heroSub}>{t('rpsRoom.pageSubtitle')}</p>
      </div>

      <div className={styles.content}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('rpsRoom.sectionTitle')}</h2>
          <button className={styles.refreshBtn} onClick={() => refetch()} title={t('rpsRoom.refresh')}>
            <RefreshCcw size={16} />
          </button>
          <Button leftIcon={<Plus size={16} />} onClick={() => setShowCreate(true)}>
            {t('rpsRoom.createRoom')}
          </Button>
        </div>

        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : rooms.length === 0 ? (
          <EmptyState
            icon={<Swords size={28} />}
            title={t('rpsRoom.emptyTitle')}
            description={t('rpsRoom.emptyDescription')}
          />
        ) : (
          <div className={styles.grid}>
            {rooms.map((room) => (
              <RoomCard key={room.room_id} room={room} onOpen={() => handleOpenRoom(room)} />
            ))}
          </div>
        )}
      </div>

      <Modal
        open={showCreate}
        onOpenChange={(open) => { setShowCreate(open); if (!open) reset(); }}
        title={t('rpsRoom.createModalTitle')}
      >
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <Input
            label={t('rpsRoom.nameLabel')}
            required
            placeholder={t('rpsRoom.namePlaceholder')}
            error={errors.name?.message}
            {...register('name')}
          />
          <Input
            label={t('rpsRoom.passwordLabel')}
            required
            placeholder={t('rpsRoom.passwordPlaceholder')}
            error={errors.password?.message}
            {...register('password')}
          />
          <div className={styles.formRow}>
            <Input
              label={t('rpsRoom.maxGamesLabel')}
              type="number"
              min={1}
              max={9}
              error={errors.max_games?.message}
              {...register('max_games')}
            />
            <Input
              label={t('rpsRoom.timeLimitLabel')}
              type="number"
              min={3}
              max={60}
              error={errors.time_limit_sec?.message}
              {...register('time_limit_sec')}
            />
          </div>
          <div className={styles.formActions}>
            <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              {t('rpsRoom.create')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function RoomCard({ room, onOpen }: { room: RPSRoomSummary; onOpen: () => void }) {
  const isWaiting = room.status === 'waiting';
  const isPlaying = room.status === 'playing';

  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <span className={`${styles.statusDot} ${isPlaying ? styles.statusPlaying : styles.statusWaiting}`} />
        <div className={styles.cardStatus}>
          {isWaiting ? t('rpsRoom.statusWaiting') : isPlaying ? t('rpsRoom.statusPlaying') : t('rpsRoom.statusEnded')}
        </div>
        {room.has_opponent ? <Badge tone="neutral">{t('rpsRoom.hasOpponent')}</Badge> : <Badge tone="warning">{t('rpsRoom.waitingOpponent')}</Badge>}
      </div>

      <h3 className={styles.cardTitle}>{room.name}</h3>

      {room.owner && (
        <div className={styles.cardMeta}>{t('rpsRoom.owner', { name: room.owner.display_name ?? '—' })}</div>
      )}

      <div className={styles.cardInfo}>
        <span className={styles.infoItem}><Swords size={13} />{t('rpsRoom.maxGames', { n: room.max_games })}</span>
        <span className={styles.infoItem}><Clock size={13} />{t('rpsRoom.timeLimit', { n: room.time_limit_sec })}</span>
      </div>

      <Button className={styles.joinBtn} variant={isPlaying ? 'secondary' : 'primary'} onClick={onOpen} leftIcon={<Users size={15} />}>
        {!room.has_opponent && isWaiting ? t('rpsRoom.joinAsPlayer') : t('rpsRoom.watchAsSpectator')}
      </Button>
    </div>
  );
}
